import styled from "@emotion/styled";
import { SmallTextInput } from "@ui/components/inputs/text-input";
import { isNil } from "@utils/assert";
import { getTwoDigitStringFromNumber } from "@utils/number-utils";
import React, { InputHTMLAttributes, useCallback, useEffect, useState } from "react";

type CustomInputProps = Omit<InputHTMLAttributes<HTMLInputElement>, "type" | "value" | "onChange" | "onDurationChange">;

interface DurationInputProps extends CustomInputProps {
	durationInSec: number;
	onDurationChange: (duration: number | undefined) => void;
	min?: number;
	max?: number;
	step?: number;
}

export const DurationInput = React.forwardRef<HTMLInputElement, DurationInputProps>(
	({ durationInSec, onDurationChange, min, max, step = 1, onKeyDown, ...otherProps }, ref) => {
		const [textDuration, setTextDuration] = useState(getTextFromDuration(durationInSec));

		useEffect(() => {
			setTextDuration(previousTextDuration =>
				getDurationInSecFromText(previousTextDuration) === durationInSec
					? previousTextDuration
					: getTextFromDuration(durationInSec),
			);
		}, [durationInSec]);

		const incrementDuration = useCallback(
			(currentStep: number) => {
				const currentDurationInSec = getDurationInSecFromText(textDuration);
				const definedDuration = (currentDurationInSec ?? durationInSec) + currentStep;
				if ((isNil(min) || definedDuration >= min) && (isNil(max) || definedDuration <= max)) {
					setTextDuration(getTextFromDuration(definedDuration));
				}
			},
			[durationInSec, max, min, textDuration],
		);

		useEffect(() => {
			const newDuration = getDurationInSecFromText(textDuration);
			onDurationChange(newDuration);
		}, [onDurationChange, textDuration]);

		return (
			<StyledTextInput
				ref={ref}
				value={textDuration}
				inputMode="numeric"
				onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => {
					if (!keyIsValid(event.key, textDuration)) {
						event.preventDefault();
					}
					if (event.key === "ArrowUp") {
						incrementDuration(step);
					} else if (event.key === "ArrowDown") {
						incrementDuration(-step);
					}
					onKeyDown?.(event);
				}}
				onChange={e => {
					setTextDuration(e.target.value);
				}}
				{...otherProps}
			/>
		);
	},
);
DurationInput.displayName = "DurationInput";

function getDurationInSecFromText(durationText: string): number | undefined {
	const durationRegex = new RegExp(/^((0?[0-5]\d|\d):?([0-5]\d))$/);
	const matchingContent = durationRegex.exec(durationText);
	if (matchingContent?.length == 4) {
		const minutes = parseInt(matchingContent[2]);
		const seconds = parseInt(matchingContent[3]);
		if (!isNaN(minutes) && !isNaN(seconds)) {
			return minutes * 60 + seconds;
		}
	}

	const secondsRegex = new RegExp(/^([0-5]?\d)$/);
	const matchingSeconds = secondsRegex.exec(durationText);
	if (matchingSeconds?.length == 2) {
		const seconds = parseInt(matchingSeconds[0]);
		if (!isNaN(seconds)) {
			return seconds;
		}
	}

	const minutesRegex = new RegExp(/^((0?[0-5]\d|\d):)$/);
	const matchingMinutes = minutesRegex.exec(durationText);
	if (matchingMinutes?.length == 3) {
		const minutes = parseInt(matchingMinutes[2]);
		if (!isNaN(minutes)) {
			return minutes * 60;
		}
	}
}

function getTextFromDuration(durationInSec: number): string {
	const minutes = Math.floor(durationInSec / 60);
	const seconds = durationInSec % 60;
	return getTwoDigitStringFromNumber(minutes) + ":" + getTwoDigitStringFromNumber(seconds);
}

const StyledTextInput = styled(SmallTextInput)`
	max-width: 2.8rem;
	align-self: center;
`;

const availableKeys = ["Tab", "Enter", "Escape", "ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight", "Backspace"];

function keyIsValid(key: string, currentText: string): boolean {
	return availableKeys.includes(key) || !isNaN(parseInt(key)) || (key === ":" && !currentText.includes(":"));
}
